import type { AssociationType } from '@/lib/crm/core/SchemaClient';
import type { PropertyTypeSchema } from '@/lib/crm/hooks/usePropertySchema';
import type { EntityIconComponent } from '@/lib/crm/utils/icons/EntityTypeIcon';
import { getAssociationTargetIconComponent } from '@/lib/crm/utils/icons/EntityTypeIcon';
import type { PropertyIconComponent } from '@/lib/crm/utils/icons/PropertyTypeIcon';
import { getPropertyTypeIconComponent } from '@/lib/crm/utils/icons/PropertyTypeIcon';

import type {
  CardFieldSlot,
  CardSlotType,
  PipelineCardConfig,
} from './cardConfig';
import { isCardFieldPickerEligible } from './cardConfig';

/**
 * Entity kinds whose associations are never useful on pipeline cards.
 */
const HIDDEN_ASSOCIATION_TARGET_KINDS = new Set([
  'pipeline', // pipeline entities (deal pipelines, note pipelines)
  'signal', // risk entities
  'empty', // file entities
  'reporting', // system-generated reporting artifacts
  'automation', // system automations
]);

/** Specific entity types to hide even if their kind would otherwise pass. */
const HIDDEN_ASSOCIATION_TARGET_TYPES = new Set([
  'ahoy_email_thread',
  'ahoy_meeting',
  'ahoy_meeting_notes',
  'ahoy_file',
  'ahoy_note',
  'ahoy_linkedin_conversation',
  'ahoy_deal_pipeline',
  'ahoy_deal_pipeline_stage',
  'ahoy_contact_lifecycle_stage',
  'ahoy_risk',
]);

export interface FieldPickerOption {
  type: CardSlotType;
  name: string;
  label: string;
  icon: PropertyIconComponent | EntityIconComponent;
  /** Property value type (e.g. "currency", "date", "text"). Absent for associations. */
  valueType?: string;
}

export type CardFieldRef = Pick<CardFieldSlot, 'type' | 'name'>;

export function buildFieldPickerOptions(
  entityType: string,
  propertySchemas: Record<string, PropertyTypeSchema>,
  associationTypes: AssociationType[],
  stageAssociationTypes: Set<string>
): { properties: FieldPickerOption[]; associations: FieldPickerOption[] } {
  const properties: FieldPickerOption[] = Object.values(propertySchemas)
    .filter(isCardFieldPickerEligible)
    .map(schema => ({
      type: 'property' as const,
      name: schema.property_type_name,
      label: schema.label || schema.property_type_name,
      icon: getPropertyTypeIconComponent(schema),
      valueType: schema.field_type_name,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  // The source filter below is type-equality (source_entity_type_name ===
  // entityType), not kind-aware. That already keeps the kind-based built-in
  // owner association (e.g. `ahoy_deal_to_owner`, source entity_kind "case",
  // empty source_entity_type_name on the frontend) out of the picker for
  // every case-kind type, deals included — no name check needed. The
  // `!== ownerAssocName` guard below only removes the legacy type-based
  // custom name (e.g. `org_feature_to_owner`).
  //
  // COUPLING: if this filter is ever made kind-aware (e.g. switched to
  // associationMatchesSource), the built-in owner would start matching and
  // must then be excluded via the kind-aware name from
  // resolveOwnerAssociationName (lib/crm/utils/ownerAssociation.ts), not by
  // the `${entityType}_to_owner` template used here.
  const ownerAssocName = `${entityType}_to_owner`;
  const associations: FieldPickerOption[] = associationTypes
    .filter(
      assoc =>
        assoc.source_entity_type_name === entityType &&
        !stageAssociationTypes.has(assoc.association_type_name) &&
        assoc.association_type_name !== ownerAssocName &&
        !HIDDEN_ASSOCIATION_TARGET_TYPES.has(assoc.target_entity_type_name) &&
        (!assoc.target_entity_kind_name ||
          !HIDDEN_ASSOCIATION_TARGET_KINDS.has(assoc.target_entity_kind_name))
    )
    .map(assoc => ({
      type: 'association' as const,
      name: assoc.association_type_name,
      label: assoc.label || assoc.association_type_name,
      icon: getAssociationTargetIconComponent(assoc),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  return { properties, associations };
}

/**
 * Build supplemental FieldPickerOption entries for properties that are
 * configured in a card layout but excluded from the normal picker (e.g.
 * markdown fields like `ahoy_content`).
 *
 * These are merged into the preview's options map so configured slots
 * render properly, but they are NOT added to the picker dropdown — users
 * cannot add new fields of these types, only see the ones already there.
 */
export function buildSupplementalPropertyOptions(
  config: PipelineCardConfig,
  propertySchemas: Record<string, PropertyTypeSchema>,
  pickerProperties: FieldPickerOption[]
): FieldPickerOption[] {
  const pickerNames = new Set(pickerProperties.map(p => p.name));

  // Collect unique property names from the config that aren't already
  // covered by the picker-eligible list.
  const seen = new Set<string>();
  const supplemental: FieldPickerOption[] = [];

  for (const row of config.rows) {
    for (const slot of row.slots) {
      if (
        slot.type === 'property' &&
        slot.name &&
        !pickerNames.has(slot.name) &&
        !seen.has(slot.name)
      ) {
        seen.add(slot.name);
        const schema = propertySchemas[slot.name];
        if (schema) {
          supplemental.push({
            type: 'property',
            name: schema.property_type_name,
            label: schema.label || schema.property_type_name,
            icon: getPropertyTypeIconComponent(schema),
            valueType: schema.field_type_name,
          });
        }
      }
    }
  }

  return supplemental;
}
